/*
 * Navigation sign-in link that returns a visitor to the public page they were reading.
 * Used by AppNavigation; the shared workflow preserves pending sign-in across link removal.
 * The link supplies its destination while authentication and scroll recovery stay reusable.
 * Example: registerComponents(registry);
 *          <PublicLogin context={context}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { servicesContext } from '../../runtime/services.js';
import { routeRequiresAuth } from '../../lib/ui/auth-policy.js';
import { createPublicLoginWorkflow } from './public-login.js';

const accountFlows = new Set([
  '/login',
  '/register',
  '/verify-email',
  '/reset-password',
  '/reset-password-request'
]);

export class PublicLogin extends UIElement {
  static properties = {
    linkId: {},
    onLogin: { type: Boolean },
    refresh: { attribute: false },
    onError: { attribute: false },
    href: { state: true }
  };

  constructor() {
    super();
    this.linkId = 'nav-login';
    this.onLogin = false;
    this.href = '/login';
  }

  willUpdate(changed) {
    super.willUpdate(changed);
    if (this.context && !this.workflow) {
      servicesContext(this.context, ['auth']);
      this.workflow = this.context.service(createPublicLoginWorkflow);
    }
    if (this.context && (changed.has('context') || changed.has('onLogin')))
      this.href = this.currentHref();
  }

  enabled() {
    const path = this.context.window.location.pathname;
    return !accountFlows.has(path) && !routeRequiresAuth(path);
  }

  currentHref() {
    const { pathname, search, hash } = this.context.window.location;
    return this.enabled()
      ? `/login?next=${encodeURIComponent(pathname + search + hash)}`
      : '/login';
  }

  location() {
    const next = this.currentHref();
    if (next === this.href) return;
    this.href = next;
    // Copy/open-link defaults read href during this event, before an async update.
    this.performUpdate();
  }

  activate(event) {
    if (!this.enabled()) return;
    this.location();
    const link = event.currentTarget;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (link.target && link.target !== '_self')
    )
      return;
    event.preventDefault();
    if (this.context.services.auth.getUser()) return;
    link.focus({ preventScroll: true });
    void this.workflow.signIn({ refresh: this.refresh, onError: this.onError });
  }

  render() {
    return (
      <a
        id={this.linkId}
        href={this.href}
        class="nav-auth-link"
        aria-current={this.onLogin ? 'page' : undefined}
        onPointerDown={this.location}
        onFocus={this.location}
        onContextMenu={this.location}
        onClick={this.activate}>
        Log in
      </a>
    );
  }
}

export function registerComponents(registry) {
  defineUIElements(registry, { 'tbc-public-login': PublicLogin });
}

// The navigation boundary still accepts an ordinary rendering function while its parent migrates.
export function publicLoginHTML(state) {
  return (
    <PublicLogin
      context={state.context}
      linkId={state.id || 'nav-login'}
      onLogin={state.onLogin}
      refresh={state.refresh}
      onError={state.onError}/>
  );
}
